import { useEffect, useMemo, useState } from "react";
import { useSession } from "../store/session.js";
import { useGameStore } from "../store/game.js";
import { useRouter } from "../lib/router.js";
import { Board } from "../components/Board.js";
import { ScoreBar } from "../components/ScoreBar.js";
import { ChatSheet } from "../components/ChatSheet.js";
import { Toast } from "../components/ui.js";
import { WaitingOverlay } from "../components/WaitingOverlay.js";
import { GameOverOverlay } from "../components/GameOverOverlay.js";
import { Button } from "../components/ui.js";
import { BombGlyphIcon, ChatIcon, ChevronLeftIcon, CraterIcon } from "../components/icons.js";

export function Game({ id }: { id: string }) {
  const { token, playerId } = useSession();
  const { navigate } = useRouter();
  const {
    conn,
    snapshot,
    notFound,
    lastReveal,
    pendingCell,
    gameOverReason,
    rematchPendingBy,
    toast,
    connect,
    disconnect,
    move,
    bomb,
    sendChat,
    rematch,
    resign,
    clearToast,
  } = useGameStore();
  const [bombArmed, setBombArmed] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const [chatSeen, setChatSeen] = useState(0);
  const chatCount = snapshot?.chat.length ?? 0;
  const unread = chatOpen ? 0 : Math.max(0, chatCount - chatSeen);

  // Mark messages seen the moment the sheet opens (or a new one arrives while open).
  useEffect(() => {
    if (chatOpen) setChatSeen(chatCount);
  }, [chatOpen, chatCount]);

  useEffect(() => {
    if (!token) {
      navigate("/", { replace: true });
      return;
    }
    connect(id, token);
    return () => disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token]);

  // Haptic + disarm feedback on each reveal.
  useEffect(() => {
    if (!lastReveal) return;
    if ("vibrate" in navigator) navigator.vibrate(lastReveal.mineHit ? [12, 40, 18] : 10);
  }, [lastReveal]);

  // Screen-reader announcements for reveals + whose turn it is.
  useEffect(() => {
    if (!lastReveal || !snapshot) return;
    const s = snapshot;
    const who = s.you !== null && lastReveal.by === s.you ? "You" : "Opponent";
    const n = lastReveal.cells.length;
    const what = lastReveal.mineHit
      ? `${who} claimed a mine.`
      : `${who} revealed ${n} tile${n === 1 ? "" : "s"}.`;
    const turn =
      s.status !== "active" ? "" : s.you !== null && s.turn === s.you ? " Your turn." : " Opponent's turn.";
    setAnnouncement(`${what} Score: red ${s.scores.red}, blue ${s.scores.blue}.${turn}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastReveal]);

  // Announce the game result.
  useEffect(() => {
    if (snapshot?.status !== "finished") return;
    const w = snapshot.winner;
    const result = w === null ? "It's a draw." : snapshot.you === w ? "You win!" : "You lost.";
    setAnnouncement(
      `Game over. ${result} Final score: red ${snapshot.scores.red}, blue ${snapshot.scores.blue}.`,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot?.status]);

  const justRevealed = useMemo(() => new Set(lastReveal?.cells ?? []), [lastReveal]);
  const you = snapshot?.you ?? null;
  const myTurn = !!snapshot && you !== null && snapshot.turn === you && snapshot.status === "active";

  function onTap(x: number, y: number) {
    if (bombArmed) {
      bomb(x, y);
      setBombArmed(false);
    } else {
      move(x, y);
    }
  }

  function handleLeave() {
    if (snapshot?.status === "active" && you !== null) {
      if (window.confirm("Leave and forfeit the game? Your opponent will win.")) {
        resign();
        navigate("/");
      }
      return;
    }
    navigate("/");
  }

  if (!snapshot) {
    if (notFound) {
      return (
        <div className="grid min-h-full place-items-center px-4">
          <div className="clay w-full max-w-sm p-6 text-center">
            <CraterIcon className="mx-auto h-14 w-14" />
            <h2 className="mt-2 text-xl font-black">Game not found</h2>
            <p className="mt-1 text-sm opacity-60">
              This game doesn't exist, or it's already finished and been cleared away.
            </p>
            <Button variant="primary" className="mt-4 w-full" onClick={() => navigate("/")}>
              Back to menu
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div className="grid min-h-full place-items-center">
        <p className="animate-pulse text-lg font-bold opacity-70">
          {conn === "error" ? "Connection problem…" : "Connecting…"}
        </p>
      </div>
    );
  }

  const waiting = snapshot.status === "waiting" || snapshot.players.length < 2;
  const finished = snapshot.status === "finished";

  return (
    <div className="mx-auto flex min-h-full w-full max-w-[30rem] flex-col px-3 pt-3 pad-safe">
      {conn !== "open" && (
        <p className="mb-1 text-center text-xs font-bold opacity-50">
          {conn === "connecting" ? "reconnecting…" : ""}
        </p>
      )}

      <ScoreBar snapshot={snapshot} you={you} onBomb={() => setBombArmed((a) => !a)} bombArmed={bombArmed} />

      <div className="relative mt-3 flex-1">
        <div
          data-testid="turn-status"
          className={`mb-2 text-center text-sm font-bold ${myTurn ? "opacity-100" : "opacity-55"}`}
        >
          {snapshot.status !== "active" ? (
            ""
          ) : myTurn ? (
            bombArmed ? (
              <span className="inline-flex items-center gap-1">
                <BombGlyphIcon className="h-4 w-4" /> Choose where to drop your bomb
              </span>
            ) : (
              "Your turn — tap to reveal"
            )
          ) : (
            "Opponent's turn…"
          )}
        </div>
        <Board
          cells={snapshot.cells}
          justRevealed={justRevealed}
          pendingCell={pendingCell}
          lastMoveIndex={lastReveal?.origin ?? null}
          lastMoveTeam={lastReveal?.by ?? null}
          interactive={myTurn}
          onTap={onTap}
          leadingControl={
            <button
              type="button"
              onClick={handleLeave}
              className="grid h-7 w-7 place-items-center opacity-40 transition hover:opacity-70"
              aria-label="Leave game"
              title="Leave game"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
          }
          trailingControl={
            <button
              type="button"
              onClick={() => setChatOpen((o) => !o)}
              className="clay-btn relative grid h-9 w-9 place-items-center"
              aria-label="Chat"
            >
              <ChatIcon className="h-5 w-5" />
              {unread > 0 && (
                <span className="absolute -right-1.5 -top-1.5 grid h-5 min-w-5 place-items-center rounded-full bg-red-500 px-1 text-[0.65rem] font-bold text-white">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </button>
          }
        />
      </div>

      <ChatSheet
        messages={snapshot.chat}
        you={you}
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        onSend={sendChat}
      />

      {waiting && !finished && (
        <WaitingOverlay
          joinCode={snapshot.joinCode}
          visibility={snapshot.visibility}
          isHost={you === "red"}
        />
      )}
      {finished && (
        <GameOverOverlay
          snapshot={snapshot}
          you={you}
          myPlayerId={playerId}
          reason={gameOverReason}
          rematchPendingBy={rematchPendingBy}
          onRematch={rematch}
          onExit={() => navigate("/")}
        />
      )}

      <Toast message={toast} onDone={clearToast} />
      <div aria-live="polite" role="status" className="sr-only">
        {announcement}
      </div>
    </div>
  );
}
